import { isFunction } from 'lodash';
import DOMPurify from 'dompurify';
import { parse } from '../../../../../../node_module_clones/sheetclip/sheetclip';
import convertHtmlToDraft from '../../../../../../common/utils/editor/convertHtmlToDraft';
import { stringifyCellContent } from '../../../../../../common/table/utils/tableCellContentStringUtils';
import globalLogger from '../../../../../logger';
import { DataTransferTypes } from '../../../../../../common/media/mimeConstants';

const logger = globalLogger.createChannel('table.dataUtils');

/**
 * Pushes an entry into the next free index in a sparse row array.
 */
const pushToNextEmptyIndex = <T>(array: Array<T>, entry: T): number => {
    const index = array.findIndex((value) => value === undefined);
    if (index < 0) return array.push(entry) - 1;

    array[index] = entry;
    return index;
};

/**
 * Parses HTML table markup into a plain grid plus column widths.
 */
export const parseTableHTML = (html: string): { values: (string | null)[][]; colWidthsPx: number[] } | null => {
    if (!/(<table)|(<TABLE)/g.test(html)) return null;

    const containerElement = document.createElement('div');
    containerElement.innerHTML = html;
    const tableElement = containerElement.querySelector('table');

    if (!tableElement) return null;

    const colWidthsPx: number[] = [];
    tableElement.querySelectorAll('colgroup > col').forEach((colElement: unknown) => {
        if (colElement instanceof HTMLTableColElement) colWidthsPx.push(parseInt(colElement.width));
    });

    const rowElements = tableElement.querySelectorAll('tr');
    const values: (string | null)[][] = [];

    rowElements.forEach((rowElement, row) => {
        values[row] = values[row] || [];

        const colMapping: { [index: number]: number } = {};
        rowElement.querySelectorAll<HTMLTableCellElement>('td, th').forEach((entryElement, col) => {
            colMapping[col] = pushToNextEmptyIndex(
                values[row],
                stringifyCellContent(convertHtmlToDraft(entryElement.innerHTML)),
            );

            const colSpan = entryElement.colSpan || 0;
            for (let index = 2; index <= colSpan; index++) {
                pushToNextEmptyIndex(values[row], null);
            }

            const rowSpan = entryElement.rowSpan || 0;
            for (let index = 2; index <= rowSpan; index++) {
                const newRow = row + index - 1;
                const newCol = colMapping[col];
                values[newRow] = values[newRow] || [];
                values[newRow][newCol] = null;
            }
        });
    });

    if (values.length === 0) return null;

    return { values, colWidthsPx };
};

/**
 * Extracts table-shaped clipboard data from HTML first, then plain text as a fallback.
 */
export const parseTableHTMLFromClipboardData = (
    clipboardData: DataTransfer,
): { values: (string | null)[][]; colWidthsPx?: number[]; plainText?: string } | null => {
    if (!clipboardData) return null;

    try {
        const rawHtml = clipboardData.getData(DataTransferTypes.HTML);
        const htmlText = rawHtml && isFunction(DOMPurify?.sanitize) ? DOMPurify.sanitize(rawHtml) : rawHtml;

        const tableData = htmlText ? parseTableHTML(htmlText) : null;
        if (tableData) return tableData;
    } catch (error) {
        logger.error('Failed to parse the html using DOMPurify', error);
    }

    const plainText = clipboardData.getData(DataTransferTypes.PLAIN_TEXT);
    const values = parse(plainText);

    return { values };
};
